// ---------------------------------------------------------------------------
// Math sanitiser (shared by the student and teacher chats)
// ---------------------------------------------------------------------------
// LLMs frequently emit half-wrapped math:
//   \begin{aligned} ... \end{aligned}$$        (missing opening $$)
//   \[ ... \] / \( ... \)                       (delimiters remark-math ignores)
//   or a leading =\frac{...} on its own line   (no delimiters at all)
// We patch the most common patterns BEFORE the markdown parser runs so KaTeX
// sees a clean block. Conservative — never deletes or rearranges content, only
// adds opening/closing $$ (or converts delimiters) where we can prove it's safe.
// Programming-ish keywords that mark a backtick span as real code, never math.
// Guards against turning a code lesson's `print(x)` / `let n = 5` into an equation.
const PROG_RE =
  /\b(print|printf|echo|def|return|var|let|const|function|import|export|class|for|while|if|else|elif|true|false|null|none|console|public|private|void|int|str|string|float|bool|new|await|async)\b/i;

// Known math function / symbol words that, on their own, signal "this is math".
const MATH_WORD_RE = /\b(sqrt|pi|theta|phi|alpha|beta|sin|cos|tan|log|ln)\b/i;

// Characters allowed inside something we're willing to treat as math.
// Includes < > : → so sequences (`36 -> 49 -> 64`), comparisons and ratios
// (`3:2`) rescue too — the guards below (length, PROG_RE, math signal) still
// keep real code out.
const MATH_CHARS_RE = /^[A-Za-z0-9\s+\-*/^=().,|_√π<>:→]+$/;

// True when a short backtick/fence body reads as a math token (e.g. "p/q",
// "pi", "sqrt(2)", "3x + 1") rather than a snippet of program code.
function looksLikeMath(t: string): boolean {
  if (!t || t.length > 40) return false;
  if (!MATH_CHARS_RE.test(t)) return false;   // stray symbols → probably code/prose
  if (PROG_RE.test(t)) return false;          // clearly a code snippet
  // Require an actual math signal so plain words ("hello") stay untouched.
  return (
    /[0-9+\-*/^=()]/.test(t) ||   // digits or an operator
    MATH_WORD_RE.test(t) ||       // sqrt / pi / sin / ...
    /^[a-zA-Z]$/.test(t)          // a lone variable letter
  );
}

// Light LaTeX-ify a plain math token so KaTeX renders it as real notation
// (radical sign, Greek letter, × symbol) instead of literal ASCII.
function mathify(t: string): string {
  return t
    .replace(/\bsqrt\s*\(([^()]*)\)/gi, (_m, inner) => `\\sqrt{${inner.trim()}}`)
    .replace(/\bpi\b/gi, '\\pi')
    .replace(/\btheta\b/gi, '\\theta')
    .replace(/\bphi\b/gi, '\\phi')
    .replace(/\balpha\b/gi, '\\alpha')
    .replace(/\bbeta\b/gi, '\\beta')
    // Sequence arrows: `36 -> 49 -> 64` reads as real math with \to.
    .replace(/\s*(?:->|→)\s*/g, ' \\to ')
    .replace(/\s*<=\s*/g, ' \\le ')
    .replace(/\s*>=\s*/g, ' \\ge ')
    .replace(/\s*\*\s*/g, ' \\times ');
}

export function sanitizeMath(raw: string): string {
  let s = raw;

  // -1) Rescue math the model wrapped in code formatting. LLMs frequently emit
  //     a bare token like `p/q`, `pi`, or ```\nsqrt(2)\n``` — which renders as a
  //     dark "terminal" code block, confusing for a student reading an equation.
  //     Convert those to inline `$…$` math so KaTeX renders them cleanly. Real
  //     code (multi-line, or containing programming keywords) is left alone.
  //
  //     Fenced blocks first (single short line only), then inline single-backtick
  //     spans. Order matters so the inline pass doesn't chew on fence markers.
  s = s.replace(/```[a-zA-Z0-9]*\n?([^\n`]{1,40})\n?```/g, (m, body) => {
    const t = String(body).trim();
    return looksLikeMath(t) ? `$${mathify(t)}$` : m;
  });
  s = s.replace(/`([^`\n]{1,40})`/g, (m, body) => {
    const t = String(body).trim();
    return looksLikeMath(t) ? `$${mathify(t)}$` : m;
  });

  // 0) Normalise LaTeX-style math delimiters the model loves to emit but
  //    `remark-math` doesn't recognise:
  //      \[ ... \]  → $$ ... $$   (display math)
  //      \( ... \)  → $ ... $     (inline math)
  //    Without this the leading backslash is consumed as a markdown escape,
  //    so the equation leaks out as raw LaTeX wrapped in literal ( ) / [ ]
  //    brackets. Non-greedy + [\s\S] so a display block can span lines.
  s = s.replace(/\\\[([\s\S]+?)\\\]/g, (_m, body) => `\n$$${body.trim()}$$\n`);
  s = s.replace(/\\\(([\s\S]+?)\\\)/g, (_m, body) => `$${body.trim()}$`);

  // 1) `\begin{aligned}` without a `$$` opener just before it.
  s = s.replace(/(^|[^$])(\\begin\{aligned\})/g, (_m, pre, beg) => `${pre}$$${beg}`);

  // 2) `\end{aligned}` without a `$$` closer just after it.
  s = s.replace(/(\\end\{aligned\})(?!\s*\$\$)/g, (_m, end) => `${end}$$`);

  // 3) A line that starts with `=\frac` or `\frac` (no delimiters around it).
  s = s.replace(/^([ \t]*=?\\frac[^\n]*)$/gm, (line) => {
    if (/^\s*\$/.test(line) || /\$\s*$/.test(line)) return line;
    return `$$${line.trim()}$$`;
  });

  // 4) Repair malformed LaTeX so KaTeX renders real notation instead of
  //    printing the raw source in its grey error colour. Runs LAST, after all
  //    `$…$` delimiters are in place. See repairMath for the exact rules.
  s = repairMath(s);

  return s;
}

// Commands whose single `{…}` argument the model most often mangles.
const CMD = 'sqrt|frac|text|overline|underline|vec|hat|bar|tilde|mathbf|mathrm';

// Fix the handful of malformed-LaTeX shapes the model actually emits. Every rule
// only fires on a clearly-broken sequence and provably leaves valid math alone
// (see mathText render tests). The symptom this kills: a lone `\sqrt{2` showing
// as grey code because KaTeX couldn't parse an unclosed / misplaced brace.
function repairMath(s: string): string {
  let t = s;

  // a) Parentheses where a brace group belongs: `\sqrt(2)` → `\sqrt{2}`.
  //    Brackets are deliberately NOT touched — `\sqrt[3]{x}` (nth root) is valid.
  t = t.replace(
    new RegExp(`\\\\(${CMD})\\s*\\(([^()]*)\\)`, 'g'),
    (_m, cmd, inner) => `\\${cmd}{${String(inner).trim()}}`,
  );

  // b) Escaped grouping brace: `\sqrt{2\}` → `\sqrt{2}`. Set notation `\{ … \}`
  //    is safe because it opens with `\{`, which this pattern never matches.
  t = t.replace(new RegExp(`(\\\\(?:${CMD})\\{[^{}]*)\\\\\\}`, 'g'), '$1}');

  // c) Closing `$` placed before the group's `}`: `$\sqrt{2$}` → `$\sqrt{2}$`.
  t = t.replace(
    new RegExp(`\\$([^$]*\\\\(?:${CMD})\\{[^{}$]*)\\$\\}`, 'g'),
    '$$$1}$$',
  );

  // d) General safety net — balance braces inside each inline `$…$` span.
  //    If a group was never closed (`$\sqrt{2$`, `$\frac{1}{2$`), append the
  //    missing `}` before the closing `$`. Only touches spans that actually
  //    contain LaTeX (a backslash or brace), so prices like `$5` and plain
  //    prose are skipped. Escaped braces `\{ \}` (set notation) aren't counted,
  //    so `$\{1,2,3\}$` stays intact.
  t = t.replace(/\$([^$\n]+?)\$/g, (m, inner) => {
    if (!/[\\{}]/.test(inner)) return m;                       // no LaTeX → skip (currency/prose)
    const opens = (inner.match(/(?<!\\)\{/g) || []).length;
    const closes = (inner.match(/(?<!\\)\}/g) || []).length;
    return opens > closes ? `$${inner}${'}'.repeat(opens - closes)}$` : m;
  });

  return t;
}
